import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Paper,
  CircularProgress,
  Divider
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { logger } from '../utils/logger';
import GoogleMapsSearch from './GoogleMapsSearch';
import { useCityContext } from '../contexts/CityContext';

const SuggestDealDialog = ({ open, onClose }) => {
  const { city, citySlug } = useCityContext();
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [dealDescription, setDealDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const handlePlaceSelected = (placeData) => {
    logger.info('Place selected:', placeData);
    setSelectedPlace(placeData);
    setError(null);
  };

  const handleDealDescriptionChange = (e) => {
    setDealDescription(e.target.value);
    // Reset error when user types
    if (error && !selectedPlace) {
      setError('Please select a business from the search results');
    } else {
      setError(null);
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    if (error && error.includes('verification')) {
      setError(null);
    }
  };

  const validateForm = () => {
    if (!selectedPlace) {
      setError('Please select a business from the search results');
      return false;
    }
    
    if (!dealDescription.trim()) {
      setError('Please describe the happy hour deal');
      return false;
    }
    
    if (!recaptchaToken) {
      setError('Please complete the verification');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const suggestionData = {
        type: 'new_deal',
        city: citySlug,
        place_data: {
          name: selectedPlace.headline || '',
          address: selectedPlace.address || '',
          phone: selectedPlace.phone || '',
          website: selectedPlace.link || '',
          place_id: selectedPlace.place_id || '',
          location: {
            lat: selectedPlace.lat || 0,
            lon: selectedPlace.lon || 0
          }
        },
        deal_description: dealDescription.trim(),
        status: 'pending',
        created_at: serverTimestamp(),
        metadata: {
          recaptcha_token: recaptchaToken
        }
      };

      await addDoc(collection(db, 'deal_suggestions'), suggestionData);
      logger.info('Deal suggestion submitted successfully');
      setSubmitSuccess(true);
      
      // Auto-close after success
      setTimeout(() => {
        onClose(true);
      }, 2000);
    } catch (error) {
      logger.error('Error submitting deal suggestion:', error);
      setError('Failed to submit suggestion. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedPlace(null);
    setDealDescription('');
    setError(null);
    setSubmitSuccess(false);
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Suggest a New Happy Hour Deal
      </DialogTitle>
      
      <DialogContent sx={{ pb: 4 }}>
        {submitSuccess ? (
          <Alert 
            severity="success" 
            sx={{ mt: 2, display: 'flex', alignItems: 'center' }}
          >
            <Box sx={{ flexGrow: 1 }}>
              Thank you! Your suggestion has been submitted successfully and will be reviewed by our team.
            </Box>
            <CircularProgress size={20} />
          </Alert>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Search for the business
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Start typing the name of a {city.name} business to find it in our database
              </Typography>
              
              <GoogleMapsSearch onPlaceSelected={handlePlaceSelected} />
            </Box>

            {/* Selected Place Display */}
            {selectedPlace && (
              <Paper 
                elevation={0} 
                sx={{ 
                  mt: 2, 
                  mb: 3, 
                  p: 2, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1,
                  backgroundColor: 'rgba(76, 175, 80, 0.08)' 
                }}
              >
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Selected Business:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedPlace.headline}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedPlace.address}
                </Typography>
                {selectedPlace.phone && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedPlace.phone}
                  </Typography>
                )}
              </Paper>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              Describe the happy hour deal
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Deal details"
              value={dealDescription}
              onChange={handleDealDescriptionChange}
              placeholder="E.g., '1+1 on drinks between 18:00-20:00, Sunday-Thursday'"
              sx={{ mb: 3 }}
              error={!!error && error.includes('deal')}
              helperText={error && error.includes('deal') ? error : ''}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
              />
            </Box>

            {error && !error.includes('deal') && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          {submitSuccess ? 'Close' : 'Cancel'}
        </Button>
        
        {!submitSuccess && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !selectedPlace || !dealDescription.trim() || !recaptchaToken}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting ? 'Submitting...' : 'Submit Suggestion'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SuggestDealDialog;